// playNotifySound — a short, synthesized "ding-dong" chime for new-message /
// new-notification toasts. WebAudio only (no binary asset ships): a lazily-
// created, module-level AudioContext plays two overlapping sine tones so it
// reads as one connected chime rather than two separate beeps.
//
// el.call('playNotifySound') — no arguments. Reads the caller's root state
// via `this` (the standard el.call binding — same convention
// shared/methods/notification.js's openNotification already uses) so it can
// honor the user's `userPrefs.notification_sound` preference (default ON;
// MemberProfile's ProfileNotifications "Notification sound" toggle writes
// it — see workspace/components/MemberProfile.js).
//
// Never throws: a missing WebAudio implementation, a suspended/blocked
// context (browser autoplay policy), or any synthesis error is swallowed so
// a chime failure can never break the toast it's attached to.

// FA204 / frank trap — `_ctx`/`_lastPlayedAt`/`_resumeListenerInstalled` are
// genuine cross-call state (no el/root argument to hang them on; must
// survive across every independent `el.call('playNotifySound')` firing) —
// re-homed onto `window.__notifySoundState` per the pointerCapability.js
// template. `_DEBOUNCE_MS`/`_getContext`/`_armResumeOnGesture`/`_tone` were
// pure/stateless-once-given-the-state-bag helpers with exactly one caller
// (this is the file's only export) — inlined below alongside it.
export function playNotifySound() {
  try {
    const root =
      this?.state?.root || (typeof this?.getRootState === 'function' ? this.getRootState() : null)
    if (root?.userPrefs?.notification_sound === false) return

    const state = (typeof window !== 'undefined' ? window : globalThis).__notifySoundState || {
      ctx: null,
      lastPlayedAt: 0,
      resumeListenerInstalled: false
    }
    ;(typeof window !== 'undefined' ? window : globalThis).__notifySoundState = state

    // Debounce a burst of realtime events (catching up on several queued
    // messages/notifications at once) down to a single chime.
    const DEBOUNCE_MS = 1500

    const now = Date.now()
    if (now - state.lastPlayedAt < DEBOUNCE_MS) return
    state.lastPlayedAt = now

    const getContext = () => {
      if (state.ctx) return state.ctx
      const Ctor =
        typeof window !== 'undefined' && (window.AudioContext || window.webkitAudioContext)
      if (!Ctor) return null
      try {
        state.ctx = new Ctor()
      } catch (_) {
        state.ctx = null
      }
      return state.ctx
    }

    // Browsers suspend a freshly-created (or backgrounded) AudioContext until
    // a user gesture resumes it. Install a ONE-TIME listener that resumes it
    // on the next pointerdown/keydown so the very first chime after
    // page-load isn't silently dropped by the autoplay policy.
    const armResumeOnGesture = (ctx) => {
      if (state.resumeListenerInstalled || typeof document === 'undefined') return
      state.resumeListenerInstalled = true
      const resume = () => {
        try {
          ctx.resume?.()
        } catch (_) {
          /* noop */
        }
      }
      document.addEventListener('pointerdown', resume, { once: true, passive: true })
      document.addEventListener('keydown', resume, { once: true })
    }

    // One tone: exponential gain envelope (fast attack, gentle decay) so it
    // reads as a soft "ding", not a harsh beep.
    const tone = (ctx, dest, freq, startAt, duration, peakGain) => {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.type = 'sine'
      osc.frequency.setValueAtTime(freq, startAt)
      gain.gain.setValueAtTime(0.0001, startAt)
      gain.gain.exponentialRampToValueAtTime(peakGain, startAt + 0.02)
      gain.gain.exponentialRampToValueAtTime(0.0001, startAt + duration)
      osc.connect(gain)
      gain.connect(dest)
      osc.start(startAt)
      osc.stop(startAt + duration + 0.02)
    }

    const ctx = getContext()
    if (!ctx) return
    if (ctx.state === 'suspended') {
      armResumeOnGesture(ctx)
      try {
        ctx.resume?.()
      } catch (_) {
        /* noop */
      }
    }

    const dest = ctx.destination
    const t0 = ctx.currentTime
    // "ding-dong": A5 (880Hz) then D6 (1174.66Hz), the second tone starting
    // while the first is still decaying so the two read as one chime.
    tone(ctx, dest, 880, t0, 0.2, 0.12)
    tone(ctx, dest, 1174.66, t0 + 0.12, 0.2, 0.1)
  } catch (_) {
    /* a chime failure must never break the toast it's attached to */
  }
}
